import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import { FBoxContainer, FHButton } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import GoalTargetItem from './GoalTargetItem';
import { FORTA_WHITE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import PlusIcon from '../../../../svgs/PlusIcon';

import type { ITreatmentPlanGoal, ITreatmentPlanGoalTarget } from '../../../../../utils/types';

interface GoalTargetsProps {
  goal: ITreatmentPlanGoal;
  targetSaving: boolean;
  onAddTarget: () => void;
  onAddTargetWithValues: (data: any) => void;
  onUpdateField: (index: number, key: string, value: any) => void;
  onSaveTarget: (index: number, target: ITreatmentPlanGoalTarget) => void;
  onDeleteTarget: (index: number) => void;
  onUpdateTargetActivation: (index: number, reason: string) => void;
  onCancelTargetChange: (index: number, target: ITreatmentPlanGoalTarget | null) => void;
}

function GoalTargets(props: GoalTargetsProps) {
  const {
    goal,
    targetSaving,
    onUpdateField,
    onAddTarget,
    onSaveTarget,
    onDeleteTarget,
    onAddTargetWithValues,
    onUpdateTargetActivation,
    onCancelTargetChange,
  } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [scrollToLastTarget, setScrollToLastTarget] = useState(false);

  const lastTargetRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (lastTargetRef.current && scrollToLastTarget) {
      lastTargetRef.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollToLastTarget(false);
    }
  }, [goal?.targets]);

  useEffect(() => {
    if (!goal) return;

    setIsEditable(isTpEditable && goal.is_active);
  }, [isTpEditable, goal]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
      {
        <FBoxContainer
          title="Targets"
          colorTheme={Themes.Primary}
          titleVariant={'h4'}
          headerStyle={{
            paddingLeft: '18px',
            paddingRight: '18px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            width: '100%',
            marginLeft: 0,
            '& h4': {
              fontSize: '18px',
            },
          }}
          contentStyle={{
            pt: 2,
            padding: 0,
          }}
        >
          {goal.targets?.map((target, index) => (
            <Box
              key={target?.id ?? index}
              ref={index === goal.targets.length - 1 ? lastTargetRef : null}
            >
              <GoalTargetItem
                index={index}
                goalId={goal?.id ?? 0}
                target={target}
                saving={targetSaving}
                goalType={goal.goal_type}
                goalTargets={goal.targets}
                goalActive={goal.is_active}
                onSave={(target) => onSaveTarget(index, target)}
                onDelete={() => onDeleteTarget(index)}
                onUpdateField={(key, value) => onUpdateField(index, key, value)}
                onAddTargetWithValues={(data) => {
                  onAddTargetWithValues(data);
                  setScrollToLastTarget(true);
                }}
                onUpdateTargetActivation={(reason: string) =>
                  onUpdateTargetActivation(index, reason)
                }
                onCancelTargetChange={(target) => onCancelTargetChange(index, target)}
              />
            </Box>
          ))}
          {isEditable && goal?.is_editing && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mt: 1, mb: 3 }}>
              <FHButton
                data-testid="addTargetButton"
                startIcon={<PlusIcon color={FORTA_WHITE} />}
                colorTheme={Themes.Primary}
                sx={{ width: 'fit-content', height: '48px' }}
                onClick={onAddTarget}
                error={goal?.targets?.length === 0}
              >
                Add target
              </FHButton>
            </Box>
          )}
        </FBoxContainer>
      }
    </Box>
  );
}

export default GoalTargets;
